import { RetrievalMethod } from '@/feature/profiles/constants';
import {
  PatientCreationState,
  PatientRequestInput,
  ProviderFacilityRetrievalInput,
  RequestActionFormsData,
} from '../types';
import type { ProviderFacilityFormData } from '../components/request-steps/request/suggested-providers-facility/validation';
import { format, subMonths, subYears } from 'date-fns';

const mapHIEDataToApi = (
  actionFormsData: RequestActionFormsData,
  selectedMethods: RetrievalMethod[],
): Partial<PatientRequestInput> => {
  return selectedMethods.includes(RetrievalMethod.HIE)
    ? {
        hie: {
          alternativePostalCodes: actionFormsData[RetrievalMethod.HIE]
            ?.alternativePostalCodes?.length
            ? actionFormsData[RetrievalMethod.HIE]?.alternativePostalCodes
            : undefined,
        },
      }
    : {};
};

const mapFacesheetsDataToApi = (
  actionFormsData: RequestActionFormsData,
  selectedMethods: RetrievalMethod[],
): Partial<PatientRequestInput> => {
  return selectedMethods.includes(RetrievalMethod.FACESHEETS)
    ? {
        facesheets:
          actionFormsData[RetrievalMethod.FACESHEETS]?.selectedFacesheets?.map(
            (facesheet) => facesheet.facesheetConfigId,
          ) || [],
      }
    : {};
};

const mapMedicationsDataToApi = (
  actionFormsData: RequestActionFormsData,
  selectedMethods: RetrievalMethod[],
): Partial<PatientRequestInput> => {
  return {
    medications:
      selectedMethods.includes(RetrievalMethod.MEDICATIONS) &&
      !!actionFormsData[RetrievalMethod.MEDICATIONS]?.retrieveMedications,
  };
};

const mapRecordUploadDataToApi = (
  actionFormsData: RequestActionFormsData,
  selectedMethods: RetrievalMethod[],
): Partial<PatientRequestInput> => {
  return selectedMethods.includes(RetrievalMethod.RECORD_UPLOAD)
    ? {
        recordUpload:
          actionFormsData[RetrievalMethod.RECORD_UPLOAD]?.uploadedFiles?.[0]
            ?.id || undefined,
      }
    : {};
};

export const calculateServiceDates = (
  serviceDateRange: '6months' | '1year' | 'custom',
  serviceStartDate?: string,
  serviceEndDate?: string,
): { startDate: string; endDate: string } => {
  const today = new Date();

  if (serviceDateRange === 'custom' && serviceStartDate && serviceEndDate) {
    return {
      startDate: serviceStartDate,
      endDate: serviceEndDate,
    };
  }

  const startDate =
    serviceDateRange === '6months'
      ? subMonths(today, 6)
      : serviceDateRange === '1year'
        ? subYears(today, 1)
        : today;

  return {
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(today, 'yyyy-MM-dd'),
  };
};

const mapProviderFacilityFormToApi = (
  formData: ProviderFacilityFormData,
): ProviderFacilityRetrievalInput => {
  const { startDate, endDate } = calculateServiceDates(
    formData.serviceDateRange,
    formData.serviceStartDate,
    formData.serviceEndDate,
  );

  const baseData = {
    ...formData,
    serviceStartDate: startDate,
    serviceEndDate: endDate,
    medicalRecordDetails: formData.medicalRecordDetails || undefined,
  };

  return baseData;
};

const mapProviderFacilityDataToApi = (
  actionFormsData: RequestActionFormsData,
  selectedMethods: RetrievalMethod[],
): Partial<PatientRequestInput> => {
  // skip if request method is not selected or action forms data is not present
  if (
    !selectedMethods.includes(RetrievalMethod.PROVIDER_FACILITY) ||
    !actionFormsData[RetrievalMethod.PROVIDER_FACILITY]
  ) {
    return {};
  }

  const { requestFrom, releasedTo, retrieveFrom } =
    actionFormsData[RetrievalMethod.PROVIDER_FACILITY];

  // skip if required fields are not present
  if (
    !requestFrom ||
    !releasedTo ||
    !retrieveFrom ||
    retrieveFrom.length === 0
  ) {
    return {};
  }

  const mappedRetrieveFrom = retrieveFrom.map((formData) =>
    mapProviderFacilityFormToApi(formData),
  );

  return {
    providerFacility: {
      requestFrom,
      releasedTo,
      retrieveFrom: mappedRetrieveFrom as [
        ProviderFacilityRetrievalInput,
        ...ProviderFacilityRetrievalInput[],
      ],
    },
  };
};

const mapReleaseDataToApi = (
  releaseData: PatientCreationState['release'],
): Partial<PatientRequestInput> => {
  const releaseFormAndDocuments =
    releaseData?.uploadedReleaseForm?.map((file) => file.id) || undefined;
  const selectedPreviousDocuments =
    releaseData?.selectedPreviousDocuments || undefined;

  return {
    ...(releaseFormAndDocuments && releaseFormAndDocuments.length > 0
      ? { releaseFormAndDocuments }
      : {}),
    ...(selectedPreviousDocuments && selectedPreviousDocuments.length > 0
      ? { selectedPreviousDocuments }
      : {}),
  };
};

const mapSubmitDataToApi = (
  submitData: PatientCreationState['submit'],
): Partial<PatientRequestInput> => {
  if (!submitData) {
    return {};
  }

  return {
    notificationEmails: submitData.notificationEmails,
    ...(submitData.referenceId && { referenceId: submitData.referenceId }),
    ...(submitData.dateNeededBy && { dateNeededBy: submitData.dateNeededBy }),
  };
};

export const mapPatientRequestStateToApi = (
  requestState: PatientCreationState,
): Partial<PatientRequestInput> => {
  const selectedMethods = requestState.request?.selectedRequestMethods || [];
  const actionFormsData = requestState.request?.actionFormsData || {};

  // Map HIE data
  const hieData = mapHIEDataToApi(actionFormsData, selectedMethods);

  // Map FACESHEETS data
  const facesheetsData = mapFacesheetsDataToApi(
    actionFormsData,
    selectedMethods,
  );

  // Map MEDICATIONS data
  const medicationsData = mapMedicationsDataToApi(
    actionFormsData,
    selectedMethods,
  );

  // Map RECORD_UPLOAD data
  const recordUploadData = mapRecordUploadDataToApi(
    actionFormsData,
    selectedMethods,
  );

  // Map PROVIDER_FACILITY data (providerFacility field)
  const providerFacilityData = mapProviderFacilityDataToApi(
    actionFormsData,
    selectedMethods,
  );

  // Map release step data
  const releaseData = mapReleaseDataToApi(requestState.release);

  // Map submit step data
  const submitData = mapSubmitDataToApi(requestState.submit);

  // Combine all mapped data
  return {
    ...hieData,
    ...facesheetsData,
    ...medicationsData,
    ...recordUploadData,
    ...providerFacilityData,
    ...releaseData,
    ...submitData,
  };
};

type ProviderFacilityFormDataWithEditFields = ProviderFacilityFormData & {
  id?: number;
  providerNpi?: string;
  facilityNpi?: string;
};

type EditExistingRetrieveFromItem = {
  id: number;
  providerName?: string | null;
  facilityName?: string | null;
  facilityNpi?: string | null;
  providerNpi?: string | null;
  specialty?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  phoneNumber?: string | null;
  faxNumber?: string | null;
  medicalRecordDetails?: string | null;
  serviceStartDate?: string;
  serviceEndDate?: string;
};

type EditNewRetrieveFromItem = {
  providerName?: string;
  facilityName?: string;
  facilityNpi?: string;
  providerNpi?: string;
  specialty?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  phoneNumber?: string;
  faxNumber?: string;
  medicalRecordDetails?: string;
  serviceStartDate: string;
  serviceEndDate: string;
};

export type EditPatientRequestRetrieveFromItem =
  | EditExistingRetrieveFromItem
  | EditNewRetrieveFromItem;

export type EditPatientRequestInput = {
  patientId: string;
  crrId: string;
  requestFrom?: string;
  releasedTo?: string;
  retrieveFrom?: EditPatientRequestRetrieveFromItem[];
  notificationEmails?: string[];
  referenceId?: string | null;
  dateNeededBy?: string | null;
  releaseFormAndDocuments?: string[];
  selectedPreviousDocuments?: string[];
};

const mapProviderFacilityFormToEditRetrieveFrom = (
  provider: ProviderFacilityFormDataWithEditFields,
): EditPatientRequestRetrieveFromItem => {
  const { startDate, endDate } = calculateServiceDates(
    provider.serviceDateRange,
    provider.serviceStartDate,
    provider.serviceEndDate,
  );

  const baseFields = {
    providerName: provider.providerName || undefined,
    facilityName: provider.facilityName || undefined,
    providerNpi: provider.providerNpi,
    facilityNpi: provider.facilityNpi,
    specialty: provider.specialty || undefined,
    address1: provider.address1 || undefined,
    address2: provider.address2 || undefined,
    city: provider.city || undefined,
    state: provider.state || undefined,
    postalCode: provider.postalCode || undefined,
    phoneNumber: provider.phoneNumber || undefined,
    faxNumber: provider.faxNumber || undefined,
    serviceStartDate: startDate,
    serviceEndDate: endDate,
  };

  // Existing entry (has id) vs new entry (no id)
  if (provider.id !== undefined) {
    return {
      id: provider.id,
      ...baseFields,
      // for editing, change it to null to clear specificMedicalRecord on the backend
      medicalRecordDetails: provider.medicalRecordDetails || null,
    };
  }

  return {
    ...baseFields,
    // for creating, omit it (undefined) when empty, which the backend treats as "Complete Medical Records"
    medicalRecordDetails: provider.medicalRecordDetails || undefined,
  };
};

/**
 * Maps the patient request state to the edit patient request input format.
 * Validation is handled by the form (react-hook-form) and the API.
 */
export const mapPatientRequestStateToEditPatientRequestInput = ({
  requestState,
  crrId,
  patientId,
}: {
  requestState: PatientCreationState;
  crrId: string;
  patientId: string;
}): EditPatientRequestInput => {
  const providerFacility =
    requestState.request?.actionFormsData?.[RetrievalMethod.PROVIDER_FACILITY];

  const retrieveFrom = (providerFacility?.retrieveFrom ||
    []) as ProviderFacilityFormDataWithEditFields[];

  return {
    patientId,
    crrId,
    requestFrom: providerFacility?.requestFrom || '',
    releasedTo: providerFacility?.releasedTo || '',
    retrieveFrom: retrieveFrom.map(mapProviderFacilityFormToEditRetrieveFrom),
    notificationEmails: requestState.submit?.notificationEmails || [],
    referenceId: requestState.submit?.referenceId || undefined,
    dateNeededBy: requestState.submit?.dateNeededBy || undefined,
    releaseFormAndDocuments:
      requestState.release?.uploadedReleaseForm?.map((f) => f.id) || undefined,
    selectedPreviousDocuments:
      requestState.release?.selectedPreviousDocuments || undefined,
  };
};
